<template>
  <modal>
    <div class="modal-header">
      <h4 class="title">นำเข้าตอน</h4>
      <button
        @click.stop="onClose"
        type="button"
        class="btn btn-link btm-sm btn-close-container">
        <i class="fas fa-times"></i>
      </button>
    </div>
    <div class="modal-body">
      <FormGroup>
        <h5 class="name">เลือกรายการ</h5>
        <SelectOptionObject
          :class="{ 'is-invalid': errorCode === 422}"
          :value="selectedProgram"
          :label="'title'"
          :options="programs"
          :maxHeight="180"
          :placeholder="`ค้นหารายการ`"
          @onChanged="onSelectProgram"
          @onSearch="loadTpbsPrograms"
        />
        <div class="invalid-feedback" v-if="errorCode === 422">
            ยังไม่ได้นำเข้ารายการ หากต้องการนำเข้ารายการ
            <router-link :to="'/programs/import'">คลิกที่นี่</router-link>
        </div>
      </FormGroup>
      <FormGroup class="mb-0">
        <h5 class="name">เลือกตอน</h5>
        <input class="form-control" :placeholder="'ค้นหาตอน'" v-model="filter.q" @keyup.enter="onSearchEpisode"/>
        <div class="wrapper-container">
          <div class="episode-list-container" v-if="!isEmpty && !isLoading">
            <div class="episode-item"
              v-for="(episode, index) in episodes"
              :key="index"
              @click="onSelectEpisode(episode.id)">
              <div class="episode-image">
                <ImageView
                  :source="episode.coverImage"
                  :imageSize="'cover160w90h'" />
              </div>
              <div class="episode-title">{{episode.title}}</div>
              <div class="checkbox-container">
                <input
                  type="checkbox"
                  class="styled-checkbox"
                />
                <span class="checkmark" :class="{ checked: hasEpisode(episode.id)}">
                  <i class="fa fa-check check-icon" />
                </span>
              </div>
            </div>
          </div>
          <div class="empty-state-container center-container" v-if="isEmpty && !isLoading">
            <div class="no-program empty-state" v-if="!selectedProgram">
              ยังไม่ได้เลือกรายการ
            </div>
            <div class="no-episode empty-state" v-if="selectedProgram && !hasEpisodesOption">
              ไม่พบตอนที่ค้นหา
            </div>
          </div>
          <div class="spinner-container center-container" v-if="isLoading">
            <i class="fa fa-spinner fa-3x fa-spin"/>
          </div>
        </div>
        <div class="pagination-container">
          <PaginationModal
            :lastPage="lastPage"
            :currentPage.sync="filter.page"
            v-if="isShowPagination"
          />
        </div>
      </FormGroup>
    </div>
    <div class="modal-footer">
      <div class="error-text" v-if="errorCode">บันทึกไม่สำเร็จกรุณาติดต่อผู้ดูแลระบบ (code: {{errorCode}})</div>
      <button
        class="btn btn-primary"
        type="button"
        @click="onSubmit"
        :disabled="!isValid">
        <i class="fa fa-spinner fa-spin mr-1" v-if="isSaving"/>นำเข้า
        <span v-if="episodeCount"> {{episodeCount}} ตอน</span>
      </button>
    </div>
  </modal>
</template>

<script>
import _ from 'lodash';
import { loadTpbsProgram } from '@/program/api';
import Modal from '@/components/Modal.vue';
import FormGroup from '@/components/FormGroup.vue';
import SelectOptionObject from '@/components/SelectOptionObject.vue';
import PaginationModal from '@/components/PaginationModal.vue';
import ImageView from '@/image/ImageView.vue';

import { loadTpbsEpisodes, importEpisodes } from './api';

export default {
  components: {
    Modal,
    FormGroup,
    SelectOptionObject,
    PaginationModal,
    ImageView,
  },
  data() {
    return {
      programs: [],
      selectedProgram: null,
      episodes: [],
      selectedEpisodes: [],
      lastPage: 1,
      errorCode: null,
      isSaving: false,
      isLoading: false,
      filter: {
        q: '',
        page: 1,
      },
    };
  },
  created() {
    this.loadTpbsPrograms();
  },
  computed: {
    isShowPagination() {
      return this.lastPage > 1;
    },
    page() {
      return this.filter.page;
    },
    isEmpty() {
      return !this.selectedProgram || !this.hasEpisodesOption;
    },
    hasEpisodesOption() {
      if (this.episodes && this.episodes.length === 0) {
        return false;
      }
      return true;
    },
    isValid() {
      if (this.errorCode) {
        return false;
      }
      if (this.selectedEpisodes && this.selectedEpisodes.length === 0) {
        return false;
      }
      return true;
    },
    episodeCount() {
      return this.selectedEpisodes.length;
    },
  },
  watch: {
    page(newPage) {
      const filter = {
        ...this.filter,
        page: newPage,
      };
      this.loadTpbsEpisodes(filter);
    },
  },
  methods: {
    async loadTpbsPrograms(filter) {
      const response = await loadTpbsProgram(filter);
      this.programs = response.data.data;
    },
    async loadTpbsEpisodes(filter) {
      if (!this.selectedProgram) return;
      this.isLoading = true;
      const query = {
        ...filter,
        programId: this.selectedProgram.id,
      };
      const response = await loadTpbsEpisodes(query);
      this.episodes = response.data.data;
      this.lastPage = response.data.last_page;
      this.isLoading = false;
    },
    onSearchEpisode() {
      this.filter.page = 1;
      this.loadTpbsEpisodes(this.filter);
    },
    onSelectProgram(program) {
      this.errorCode = null;
      if (program) {
        this.selectedProgram = program;
        const filter = {
          programId: program.id,
        };
        this.loadTpbsEpisodes(filter);
      } else {
        this.selectedProgram = null;
        this.clearEpisodes();
      }
    },
    clearEpisodes() {
      this.episodes = [];
      this.lastPage = 1;
    },
    hasEpisode(episodeId) {
      const index = this.selectedEpisodes.indexOf(episodeId);
      if (index > -1) {
        return true;
      }
      return false;
    },
    onSelectEpisode(episodeId) {
      if (this.hasEpisode(episodeId)) {
        const index = this.selectedEpisodes.indexOf(episodeId);
        if (index >= 0) {
          this.selectedEpisodes.splice(index, 1);
        }
        return;
      }
      this.selectedEpisodes.push(episodeId);
    },
    async onSubmit() {
      try {
        this.isSaving = true;
        await importEpisodes(this.selectedEpisodes);
        this.isSaving = false;
        this.onClose();
      } catch (error) {
        this.errorCode = error.response.status;
        this.selectedEpisodes = [];
        if (this.errorCode === 500) {
          this.selectedProgram = null;
        }
        this.isSaving = false;
      }
    },
    onClose() {
      this.$router.push('/episodes');
    },
    getCoverImage(episode) {
      return _.get(episode, 'coverImage.urls.cover160w90h', null);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';

.error-text {
  font-size: 80%;
  color: #dc3545;
}

.wrapper-container {
  height: 300px;
  border-bottom: 0.5px solid $gray-200;
  border-left: 0.5px solid $gray-200;
  border-right: 0.5px solid $gray-200;
  overflow: auto;

  .episode-item {
    display: flex;
    align-items: center;
    padding: 0.75 * $spacer $spacer;
    cursor: pointer;
    .episode-image {
      width: 25%;
    }
    .episode-title {
      width: 60%;
      padding-left: $spacer;
    }
    .checkbox-container {
      margin-left: auto;
      position: relative;
      height: 30px;
      width: 30px;
      .styled-checkbox {
        position: relative;
        opacity: 0;
        height: 0;
        width: 0;
      }
      .checkmark {
        position: absolute;
        display: flex;
        justify-content: center;
        align-items: center;
        height: 30px;
        width: 30px;
        top: 0;
        right: 0;
        border-radius: 30px;
        border: 2px solid $gray-300;
        .check-icon {
          display: none;
        }
      }
      .checked {
        transition: all 0.2s ease-in;
        border: 2px solid $success;
        color: $success;
        .check-icon {
          display: inline;
        }
      }
    }
  }
  .episode-item:hover {
    transition: all 0.2s ease-in;
    background-color: $gray-100;
  }
  .center-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 300px;
  }
}

.pagination-container {
  margin-top: $spacer;
  height: 40px;
}

@media (max-width: 575.98px) {
  .episode-title {
    font-size: $font-size-base * 0.8;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

</style>
